import {
    FavoriteCreateRequest,
    FavoriteResponse,
    FavoriteType,
} from '@kcftech/prometheus-client';
import { QueryObserverResult, useMutation, useQuery } from '@tanstack/react-query';
import { minuteMs } from 'components/date_time/time';
import { sdClient } from 'contexts/SDKClientContext';
import { IDataHookProps } from 'interfaces/IDataHookProps';
import { PagedResults } from 'utils/paged_results';
import { queryClient } from 'utils/query';

/**
 * The unique query key used for caching and invalidating query results.
 */
export const favoritesQueryKey = 'favorites';

export function useFavorites({
    page,
    pageLimit,
    filter,
    nameSearch,
    orderBy,
}: IDataHookProps): QueryObserverResult<PagedResults<FavoriteResponse>> {
    return useQuery({
        queryKey: [favoritesQueryKey, page, pageLimit, filter, nameSearch, orderBy],
        queryFn: () => sdClient.getFavorites({ filter, page, pageLimit, orderBy }),
        staleTime: 30 * minuteMs,
        gcTime: minuteMs,
    });
}

export type CreateFavoriteRequest = {
    /**
     * The ID of the entity to add to favorites.
     */
    id: string;
    /**
     * The type of the entity being added to the favorites (e.g., `Dashboard`, `Location`, etc.).
     */
    type: keyof typeof FavoriteType;
};

/**
 * Create a user favorite for the given ID and type.
 */
export function createFavorite({ id, type }: CreateFavoriteRequest) {
    return sdClient.createFavorite({
        body: new FavoriteCreateRequest({
            FavoriteId: id,
            FavoriteType: FavoriteType[type],
        }),
    });
}

/**
 * Data mutation hook to create a favorite declaratively and render accordingly.
 */
export function useCreateFavorite() {
    return useMutation({
        meta: { name: 'create-favorite' },
        mutationFn: createFavorite,

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [favoritesQueryKey],
            });
        },
    });
}

export type DeleteFavoriteRequest = {
    /**
     * The ID of the entity to remove favorites.
     */
    id: string;
    /**
     * The type of favorite to delete (e.g., `Dashboard`, `Location`, etc.).
     */
    type: keyof typeof FavoriteType;
};

/**
 * Delete a user favorite with the given ID and type.
 */
export function deleteFavorite({ id, type }: DeleteFavoriteRequest) {
    return sdClient.deleteFavorite({ favoriteType: FavoriteType[type], favoriteId: id });
}

/**
 * Data mutation hook to delete a favorite declaratively and render accordingly.
 */
export function useDeleteFavorite() {
    return useMutation({
        meta: { name: 'delete-favorite' },
        mutationFn: deleteFavorite,

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [favoritesQueryKey],
            });
        },
    });
}
